/**
 * Funzione che converte una data in formato ISO
 * @param {*} date
 * @returns {string}
 */
const convertDateToISO = (date) => {
  let newDate = new Date(date);
  newDate = newDate.setHours(newDate.getHours() - (newDate.getTimezoneOffset() / 60));
  return new Date(newDate).toISOString();
};

/**
 * Verifica se la stringa in input può essere trasformata in una data
 * @param {string} input Valore da analizzare
 * @param {boolean} future Indica se voglio inserire date nel futuro e quindi preinserire l'anno 2000 se indico la data con due cifre
 * @param {boolean} decrease Indica se decrementare il mese(in quanto l'utente ad esempio per indicare gennaio indica 1 e non 0)
 * @param {string | undefined} annoDefault Indica se settare un anno di default indipendentemente da quello digitato
 * @returns {Date | null} data.
 */
const parseDate = (input, future, decrease, annoDefault) => {
  const parts = input.match(/(\d+)/g);
  if (!annoDefault) {
    if (parts == null || (parts.length !== 3 && parts.length !== 1)) {
      return null;
    }
  } else if (parts == null || (parts.length !== 3 && parts.length !== 2 && parts.length !== 1)) {
    return null;
  }

  let year;
  let month;
  let day;
  if (parts.length === 3 || (parts.length === 2 && annoDefault)) {
    year = annoDefault || parseInt(parts[2], 10);
    month = parseInt(parts[1], 10);
    day = parseInt(parts[0], 10);
  } else {
    if (parts[0].length !== 6 && parts[0].length !== 8 && !annoDefault) {
      return null;
    }

    day = parseInt(parts[0].substring(0, 2), 10);
    month = parseInt(parts[0].substring(2, 4), 10);
    year = annoDefault || parseInt(parts[0].substring(4), 10);
  }

  if (year < 100) {
    if (future) {
      year += 2000;
    } else {
      const d = new Date();
      if (year > d.getFullYear() % 100) {
        year += 1900;
      } else {
        year += 2000;
      }
    }
  }

  if (year < 1000 || year > 9999) {
    return null; // to avoid something like 202020 or 202
  }
  if (decrease) {
    month -= 1;
  }

  if (Object.prototype.toString.call(new Date(year, month - 1, day)) === '[object Date]') {
    return new Date(year, month, day);
  }

  return null;
};

/**
 * Formatta la data secondo il formato dd/MM/yyyy
 * @param {String|Number} day - Giorno
 * @param {String|Number} month - Mese
 * @param {String|Number} year - Anno
 * @param {String} separator - Separatore
 */
const formatDate = (day, month, year, separator) => `${(`00${day}`).slice(-2)}${separator}${(`00${month}`).slice(-2)}${separator}${year}`;

/**
 * Verifica se la stringa in input può essere trasformata in un mese/anno
 * @param {string} input Valore da analizzare
 * @param {boolean} future Indica se voglio inserire date nel futuro e quindi preinserire l'anno 2000 se indico la data con due cifre
 * @param {boolean} decrease Indica se decrementare il mese(in quanto l'utente ad esempio per indicare gennaio indica 1 e non 0)
 * @param {string | undefined} annoDefault Indica se settare un anno di default indipendentemente da quello digitato
 * @returns {object | null} data.
 */
// eslint-disable-next-line default-param-last
const parseMonth = (input = '', future = true, decrease, annoDefault) => {
  const parts = input.match(/(\d+)/g);
  if (parts == null || (parts.length !== 2 && parts.length !== 1)) {
    return null;
  }

  let year;
  let month;
  if (parts.length === 2) {
    year = annoDefault ? parseInt(annoDefault, 10) : parseInt(parts[1], 10);
    month = parseInt(parts[0], 10);
  } else {
    if (parts[0].length !== 4 && parts[0].length !== 6 && !annoDefault) {
      return null;
    }

    month = parseInt(parts[0].substring(0, 2), 10);
    year = annoDefault ? parseInt(annoDefault, 10) : parseInt(parts[0].substring(2), 10);
  }

  if (year < 100) {
    if (future) {
      year += 2000;
    } else {
      const d = new Date();
      if (year > d.getFullYear() % 100) {
        year += 1900;
      } else {
        year += 2000;
      }
    }
  }

  if (year < 1000 || year > 9999) {
    return null; // to avoid something like 202020 or 202
  }

  if (decrease) {
    month -= 1;
  }

  return { year, month };
};

function getTimezoneOffset() {
  return new Date().getTimezoneOffset();
}

const pad = (number) => (Number(number) < 10 ? `0${Number(number)}` : number);

/**
 * Converte una data dal formato ISO a stringa
 * @param {*} data stringa con data in formato ISO
 * @param {*} time indica se necessaria anche il dato di HH:MM
 * @returns formato DD/MM/YYYY
 */
const parseIsoDateToString = (data, time = false) => {
  if (!data || typeof data !== 'string' || !data.match(/((\+|-)\d{2}:\d{2}|Z)$/gm)) return '';
  const dataOraArray = data.split('T');
  const dataSplit = dataOraArray[0].split('-');
  const oraSplit = dataOraArray[1].split(':');
  const timeFormatted = time ? ` ${oraSplit[0]}:${oraSplit[1]}` : '';
  return `${pad(dataSplit[2])}/${pad(dataSplit[1])}/${dataSplit[0]}${timeFormatted}`;
};

const addZero = (value) => (value < 10 ? `0${value}` : value);

function formatTime(date) {
  const hour = addZero(date.getHours());
  const minutes = addZero(date.getMinutes());
  return `${hour}:${minutes}`;
}

export {
  convertDateToISO,
  parseDate,
  parseMonth,
  formatDate,
  formatTime,
  getTimezoneOffset,
  parseIsoDateToString,
};
